import { useState, useMemo, Suspense, lazy, useCallback, useEffect } from "react";
import AppHeader from "@/components/AppHeader";
import { elements } from "@/data/elements";
import EmissionSpectrum, { type SpectrumLine } from "@/components/EmissionSpectrum";
import { Button } from "@/components/ui/button";
import { Columns2, Square } from "@/lib/icons";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

const AtomViewer = lazy(() => import("@/components/AtomViewer"));

const SHELL_LETTERS = ["K", "L", "M", "N", "O", "P", "Q"];

const SUPERSCRIPT_DIGITS: Record<string, string> = {
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
};
const toSuperscript = (n: number) =>
  String(n)
    .split("")
    .map((d) => SUPERSCRIPT_DIGITS[d] ?? d)
    .join("");

interface AtomPanelProps {
  selectedNumber: number;
  onSelect: (n: number) => void;
  spectrumLines: SpectrumLine[];
  onPhoton: (p: { from: number; to: number; nm: number; color: string }) => void;
  onClearSpectrum: () => void;
  selectId: string;
  height: number | string;
  compact?: boolean;
}

function AtomPanel({
  selectedNumber,
  onSelect,
  spectrumLines,
  onPhoton,
  onClearSpectrum,
  selectId,
  height,
  compact,
}: AtomPanelProps) {
  const element = useMemo(
    () => elements.find((e) => e.number === selectedNumber)!,
    [selectedNumber],
  );
  const [showIsotope, setShowIsotope] = useState(false);
  const massNumber = Math.round(element.mass);

  return (
    <div className="space-y-3 min-w-0">
      {/* Top controls row: selector + symbol + shell pills */}
      <div className="flex flex-wrap items-center gap-3 min-w-0">
        <label htmlFor={selectId} className="sr-only">
          Select an element
        </label>
        <select
          id={selectId}
          value={selectedNumber}
          onChange={(e) => onSelect(Number(e.target.value))}
          className="h-9 min-w-0 flex-1 sm:flex-none sm:w-56 rounded-lg border border-input bg-tile-bg px-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {elements.map((el) => (
            <option key={el.number} value={el.number}>
              {el.number}. {el.name} ({el.symbol})
            </option>
          ))}
        </select>
        <div className="flex items-baseline gap-1.5">
          <span className={compact ? "text-xl font-bold text-foreground" : "text-2xl font-bold text-foreground"}>
            {element.symbol}
          </span>
          <span className="font-mono text-xs text-muted-foreground">Z = {element.number}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap ml-auto">
          {element.shells.map((count, i) => (
            <div key={i} className="flex items-center gap-1">
              <div className="w-7 h-7 rounded-full border-2 border-primary/40 flex items-center justify-center">
                <span className="font-mono text-[11px] font-semibold text-foreground">{count}</span>
              </div>
              <span className="text-[10px] text-muted-foreground">{SHELL_LETTERS[i]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 3D Viewer */}
      <div
        className="relative rounded-lg border border-border bg-tile-bg overflow-hidden"
        style={{ height }}
      >
        <Suspense
          fallback={
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
              Loading 3D viewer…
            </div>
          }
        >
          <AtomViewer
            shells={element.shells}
            symbol={element.symbol}
            protons={element.number}
            neutrons={Math.round(element.mass) - element.number}
            onPhotonEmitted={onPhoton}
          />
        </Suspense>
      </div>

      {/* Nucleon caption */}
      <div className="flex items-center gap-2 flex-wrap">
        <p
          className="text-[11px] font-mono text-muted-foreground flex flex-wrap items-center gap-x-1.5 gap-y-0.5"
          aria-live="polite"
        >
          <span>
            <span className="text-[hsl(var(--destructive))] font-semibold">{element.number}</span>{" "}
            protons
          </span>
          <span aria-hidden="true">·</span>
          <span>
            <span className="text-foreground font-semibold">
              {massNumber - element.number}
            </span>{" "}
            neutrons
          </span>
          <span aria-hidden="true">·</span>
          <span>
            <span className="text-foreground font-semibold">{massNumber}</span> mass number
          </span>
          {showIsotope && (
            <>
              <span aria-hidden="true">·</span>
              <span
                className="text-foreground font-semibold"
                aria-label={`Isotope notation: ${element.symbol} ${massNumber}`}
              >
                {toSuperscript(massNumber)}
                {element.symbol}
              </span>
            </>
          )}
        </p>
        <button
          type="button"
          onClick={() => setShowIsotope((v) => !v)}
          aria-pressed={showIsotope}
          className="text-[10px] px-1.5 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {showIsotope ? "Hide isotope" : "Show isotope"}
        </button>
      </div>

      <p className="text-xs text-muted-foreground">
        Electron configuration:{" "}
        <span className="font-mono break-all">{element.electronConfig}</span>
      </p>

      {/* Spectrum */}
      <EmissionSpectrum
        lines={spectrumLines}
        onClear={onClearSpectrum}
        exportName={`${element.symbol}_${element.name}`}
      />
    </div>
  );
}

export default function BohrModel() {
  const [compare, setCompare] = useState(false);
  const [selectedA, setSelectedA] = useState(1);
  const [selectedB, setSelectedB] = useState(11); // Sodium - different colors than H
  const [linesA, setLinesA] = useState<SpectrumLine[]>([]);
  const [linesB, setLinesB] = useState<SpectrumLine[]>([]);

  // Reset each spectrum when its element changes
  useEffect(() => setLinesA([]), [selectedA]);
  useEffect(() => setLinesB([]), [selectedB]);

  const accumulate = useCallback(
    (
      setter: React.Dispatch<React.SetStateAction<SpectrumLine[]>>,
    ) =>
      (photon: { from: number; to: number; nm: number; color: string }) => {
        setter((prev) => {
          const idx = prev.findIndex(
            (l) => l.from === photon.from && l.to === photon.to,
          );
          if (idx >= 0) {
            const next = prev.slice();
            next[idx] = { ...next[idx], count: next[idx].count + 1 };
            return next;
          }
          return [...prev, { ...photon, count: 1 }];
        });
      },
    [],
  );

  const handlePhotonA = useMemo(() => accumulate(setLinesA), [accumulate]);
  const handlePhotonB = useMemo(() => accumulate(setLinesB), [accumulate]);

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Bohr Model & Atomic Structure Viewer — ElementPad" description="Interactive 3D atom viewer with electron shells, orbitals, and emission spectra for every element on the periodic table." path="/atom" />
      <AppHeader />
      <Main className={compare ? "max-w-7xl mx-auto px-4 py-8" : "max-w-4xl mx-auto px-4 py-8"}>
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">3D Bohr Atom Model</h1>
            <p className="text-sm text-muted-foreground">
              Interactive 3D visualization with electron shells and emission spectra.
            </p>
          </div>
          <Button
            variant={compare ? "default" : "outline"}
            size="sm"
            onClick={() => setCompare((v) => !v)}
            aria-pressed={compare}
            className="gap-1.5"
          >
            {compare ? <Square className="h-4 w-4" /> : <Columns2 className="h-4 w-4" />}
            {compare ? "Single view" : "Compare two elements"}
          </Button>
        </div>

        <div
          className={
            compare
              ? "grid grid-cols-1 lg:grid-cols-2 gap-6"
              : "grid grid-cols-1 gap-6"
          }
        >
          <AtomPanel
            selectId="element-select-a"
            selectedNumber={selectedA}
            onSelect={setSelectedA}
            spectrumLines={linesA}
            onPhoton={handlePhotonA}
            onClearSpectrum={() => setLinesA([])}
            height={
              compare
                ? "clamp(240px, 38vh, 360px)"
                : "clamp(280px, 46vh, 440px)"
            }
            compact={compare}
          />
          {compare && (
            <AtomPanel
              selectId="element-select-b"
              selectedNumber={selectedB}
              onSelect={setSelectedB}
              spectrumLines={linesB}
              onPhoton={handlePhotonB}
              onClearSpectrum={() => setLinesB([])}
              height="clamp(240px, 38vh, 360px)"
              compact
            />
          )}
        </div>
      </Main>
    </div>
  );
}
